/**
 * ImageCropperSheet — WhatsApp-style square crop with zoom + drag (V1.9.33).
 *
 * Props:
 *   file       : File | null    (source image; opens sheet when set)
 *   onCancel   : () => void
 *   onCropped  : (blob: Blob) => void
 *   title      : string          (sheet header)
 *
 * Shows a live circular preview of the crop. User can pan (drag inside
 * the circle) and zoom (slider). Save produces a 512×512 PNG.
 */
import React from "react";
import BottomSheet from "@/components/BottomSheet";
import { cropToSquareBlob } from "@/lib/imageCrop";

export default function ImageCropperSheet({ file, onCancel, onCropped, title = "Adjust photo" }) {
    const [src, setSrc] = React.useState(null);
    const [scale, setScale] = React.useState(1);
    const [dx, setDx] = React.useState(0);
    const [dy, setDy] = React.useState(0);
    const [busy, setBusy] = React.useState(false);
    const [natural, setNatural] = React.useState({ w: 0, h: 0 });
    const dragRef = React.useRef({ active: false, sx: 0, sy: 0, dx0: 0, dy0: 0 });

    React.useEffect(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        setSrc(url);
        setScale(1); setDx(0); setDy(0);
        // Prefetch natural size so drag can clamp inside bounds.
        const img = new Image();
        img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
        img.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const clampDx = (v) => {
        const base = Math.min(natural.w, natural.h) / scale;
        const max = (natural.w - base) / 2;
        return Math.max(-max, Math.min(max, v));
    };
    const clampDy = (v) => {
        const base = Math.min(natural.w, natural.h) / scale;
        const max = (natural.h - base) / 2;
        return Math.max(-max, Math.min(max, v));
    };

    const onPointerDown = (e) => {
        dragRef.current = { active: true, sx: e.clientX, sy: e.clientY, dx0: dx, dy0: dy };
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    };
    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d.active || !natural.w) return;
        // Drag maps preview px → source px roughly (source is ~2× preview at scale=1).
        const factor = Math.min(natural.w, natural.h) / 260;
        setDx(clampDx(d.dx0 - (e.clientX - d.sx) * factor / scale));
        setDy(clampDy(d.dy0 - (e.clientY - d.sy) * factor / scale));
    };
    const onPointerUp = () => { dragRef.current.active = false; };

    const save = async () => {
        if (!src) return;
        setBusy(true);
        try {
            const blob = await cropToSquareBlob(src, { size: 512, scale, dx, dy });
            onCropped(blob);
        } finally { setBusy(false); }
    };

    if (!file) return null;
    // Live preview transform values.
    const previewSize = 260;
    const naturalSquare = Math.min(natural.w, natural.h) || 1;
    // Scale of the image inside the preview at zoom=1: previewSize / naturalSquare.
    const baseFit = previewSize / naturalSquare;
    const finalScale = baseFit * scale;
    // Translate preview by dx,dy converted to preview px.
    const tx = -dx * baseFit;
    const ty = -dy * baseFit;

    return (
        <BottomSheet open={!!file} onClose={onCancel} title={title}>
            <div className="px-4 pb-4 flex flex-col items-center">
                <div
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    data-testid="image-cropper-preview"
                    className="relative w-[260px] h-[260px] rounded-full overflow-hidden ring-4 ring-ink/10 bg-ink/5 select-none touch-none cursor-grab active:cursor-grabbing"
                >
                    {src && (
                        <img
                            src={src}
                            alt="crop preview"
                            draggable={false}
                            style={{
                                position: "absolute",
                                left: "50%", top: "50%",
                                width: natural.w, height: natural.h,
                                transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(${finalScale})`,
                                transformOrigin: "center center",
                                userSelect: "none", pointerEvents: "none",
                            }}
                        />
                    )}
                </div>
                <div className="w-full max-w-[280px] mt-4">
                    <label className="text-[11px] font-black uppercase tracking-wider text-ink/50 block mb-1">Zoom</label>
                    <input
                        type="range" min="1" max="3" step="0.05"
                        value={scale}
                        onChange={(e) => { setScale(Number(e.target.value)); setDx((v) => clampDx(v)); setDy((v) => clampDy(v)); }}
                        data-testid="image-cropper-zoom"
                        className="w-full accent-cobalt"
                    />
                </div>
                <div className="w-full max-w-[280px] mt-4 flex gap-2">
                    <button
                        type="button" onClick={onCancel}
                        className="flex-1 h-10 rounded-full bg-black/5 text-ink/70 text-[13px] font-bold hover:bg-black/10"
                        data-testid="image-cropper-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button" onClick={save} disabled={busy}
                        className="flex-1 h-10 rounded-full bg-emerald-500 text-white text-[13px] font-bold hover:bg-emerald-600 disabled:opacity-40"
                        data-testid="image-cropper-save"
                    >
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </BottomSheet>
    );
}
